import React from "react";
import {
  Heart,
  MessageCircle,
  Share2,
  TrendingUp,
  MoreHorizontal,
} from "lucide-react";
import styled from "styled-components";
import { apiGet, apiPost } from "../../api";
import { router } from "@inertiajs/react";

const Container = styled.div`
  position: sticky;
  top: 16px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-bottom: 2rem;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: var(--secondary);
  border-radius: 9999px;
  border: 1px solid transparent;
  transition: all 0.2s;

  &:focus-within {
    background: var(--card);
    border-color: var(--primary);
  }
`;

const SearchInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: 0.9375rem;
  color: var(--foreground);

  &::placeholder {
    color: var(--muted-foreground);
  }
`;

const Card = styled.div`
  background: var(--card);
  border-radius: 1rem;
  border: 1px solid var(--border);
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
`;

const CardTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--foreground);
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--muted-foreground);

  &:hover {
    background: var(--secondary);
  }
`;

const TrendItem = styled.div`
  width: 100%;
  padding: 0.75rem 1rem;
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition: background 0.2s;

  &:hover {
    background: var(--secondary);
  }
`;

const TrendHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TrendCategory = styled.span`
  font-size: 0.8125rem;
  color: var(--muted-foreground);
`;

const TrendTitle = styled.div`
  font-weight: 700;
  color: var(--foreground);
  font-size: 0.9375rem;
  margin-top: 0.125rem;
`;

const TrendPosts = styled.div`
  font-size: 0.8125rem;
  color: var(--muted-foreground);
  margin-top: 0.125rem;
`;

const MoreButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--muted-foreground);
  opacity: 0;
  transition: all 0.2s;

  ${TrendItem}:hover & {
    opacity: 1;
  }

  &:hover {
    background: rgba(var(--primary-rgb), 0.1);
    color: var(--primary);
  }
`;

const ShowMore = styled.button`
  width: 100%;
  padding: 1rem;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  color: var(--primary);
  font-size: 0.9375rem;
  transition: background 0.2s;

  &:hover {
    background: var(--secondary);
  }
`;

const ContributorItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: var(--secondary);
  }
`;

const ContributorAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary), #ffb347);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  span {
    font-size: 0.875rem;
    font-weight: 700;
    color: white;
  }
`;

const ContributorInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ContributorName = styled.div`
  font-weight: 700;
  color: var(--foreground);
  font-size: 0.9375rem;
`;

const ContributorHandle = styled.div`
  font-size: 0.8125rem;
  color: var(--muted-foreground);
`;

const FollowButton = styled.button`
  padding: 0.375rem 1rem;
  border-radius: 9999px;
  background: var(--foreground);
  color: var(--background);
  font-weight: 700;
  font-size: 0.875rem;
  border: none;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  padding: 0.75rem 1rem;
`;

const StatCard = styled.div`
  background: var(--secondary);
  border-radius: 0.75rem;
  padding: 0.75rem;
  text-align: center;

  svg {
    margin: 0 auto 0.25rem;
    color: ${(props) => props.$iconColor || "var(--primary)"};
  }
`;

const StatValue = styled.div`
  font-weight: 700;
  font-size: 1rem;
  color: var(--foreground);
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: var(--muted-foreground);
`;

const FooterLinks = styled.div`
  padding: 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

function formatCount(n) {
  if (n == null) return "0";
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

const FooterLink = styled.a`
  font-size: 0.8125rem;
  color: var(--muted-foreground);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export default function TrendingPanel({ onCategoryClick }) {
  const [topics, setTopics] = React.useState([]);
  const [suggestedUsers, setSuggestedUsers] = React.useState([]);
  const [followedIds, setFollowedIds] = React.useState(new Set());
  const [topicsLoaded, setTopicsLoaded] = React.useState(false);
  const [usersLoaded, setUsersLoaded] = React.useState(false);
  const [stats, setStats] = React.useState(null);

  React.useEffect(() => {
    apiGet("/api/v1/communities/sidebar").then((res) => {
      if (res.parsedResponse?.topics) setTopics(res.parsedResponse.topics);
      if (res.parsedResponse?.stats) setStats(res.parsedResponse.stats);
      setTopicsLoaded(true);
    });
    apiGet("/api/v1/communities/users/suggested").then((res) => {
      if (res.parsedResponse?.users) setSuggestedUsers(res.parsedResponse.users);
      setUsersLoaded(true);
    });
  }, []);

  const handleFollow = async (user) => {
    setFollowedIds((prev) => new Set([...prev, user.id]));
    try {
      await apiPost(`/api/v1/communities/users/${user.id}/follow`);
    } catch {
      setFollowedIds((prev) => { const s = new Set(prev); s.delete(user.id); return s; });
    }
  };

  const displayTrends = topics;
  const displayContributors = suggestedUsers;

  const visibleTrends = displayTrends.slice(0, 3);

  return (
    <Container>
      {/* What's happening */}
      <Card>
        <CardHeader>
          <CardTitle>What{"'"}s happening</CardTitle>
        </CardHeader>

        {visibleTrends.length === 0 && (
          <TrendItem style={{ cursor: "default" }}>
            <TrendCategory>{topicsLoaded ? "No trending topics yet" : "Loading trends..."}</TrendCategory>
          </TrendItem>
        )}
        {visibleTrends.map((trend) => (
          <TrendItem
            key={trend.id}
            onClick={() => {
              if (onCategoryClick) onCategoryClick(trend.slug || trend.name);
              else router.visit(`/communities/explore?topic=${encodeURIComponent(trend.slug || trend.name)}`);
            }}
            style={{ cursor: "pointer" }}
          >
            <TrendHeader>
              <TrendCategory>
                Trending <TrendingUp size={12} style={{ display: "inline", marginLeft: 4 }} />
              </TrendCategory>
              <MoreButton onClick={(e) => e.stopPropagation()}>
                <MoreHorizontal size={16} />
              </MoreButton>
            </TrendHeader>
            <TrendTitle>{trend.name}</TrendTitle>
            <TrendPosts>{(trend.posts || 0).toLocaleString()} posts</TrendPosts>
          </TrendItem>
        ))}

        <ShowMore onClick={() => router.visit('/communities/explore')}>Show more</ShowMore>
      </Card>

      {/* Who to follow */}
      <Card>
        <CardHeader>
          <CardTitle>Who to follow</CardTitle>
        </CardHeader>

        {displayContributors.length === 0 && (
          <ContributorItem style={{ cursor: "default" }}>
            <ContributorInfo>
              <ContributorHandle>{usersLoaded ? "No suggestions available" : "Loading suggestions..."}</ContributorHandle>
            </ContributorInfo>
          </ContributorItem>
        )}
        {displayContributors.map((user) => (
          <ContributorItem
            key={user.id}
            onClick={() => router.visit(`/communities/profiles/${user.slug}`)}
          >
            <ContributorAvatar>
              {user.avatar_url ? (
                <img
                  src={user.avatar_url}
                  alt={user.name}
                  style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }}
                />
              ) : (
                <span>{user.name?.charAt(0)}</span>
              )}
            </ContributorAvatar>
            <ContributorInfo>
              <ContributorName>{user.name}</ContributorName>
              <ContributorHandle>{user.handle}</ContributorHandle>
            </ContributorInfo>
            <FollowButton
              onClick={(e) => { e.stopPropagation(); if (!followedIds.has(user.id)) handleFollow(user); }}
              style={followedIds.has(user.id) ? { background: "transparent", border: "1px solid var(--border)", color: "var(--foreground)" } : {}}
            >
              {followedIds.has(user.id) ? "Following" : "Follow"}
            </FollowButton>
          </ContributorItem>
        ))}

        <ShowMore onClick={() => router.visit('/communities/explore')}>Show more</ShowMore>
      </Card>

      {/* Forum Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Forum Activity</CardTitle>
        </CardHeader>
        <StatsGrid>
          <StatCard>
            <MessageCircle size={16} />
            <StatValue>{formatCount(stats?.discussions)}</StatValue>
            <StatLabel>Discussions</StatLabel>
          </StatCard>
          <StatCard>
            <Share2 size={16} />
            <StatValue>{formatCount(stats?.shares)}</StatValue>
            <StatLabel>Shares</StatLabel>
          </StatCard>
          <StatCard $iconColor="#ef4444">
            <Heart size={16} />
            <StatValue>{formatCount(stats?.likes)}</StatValue>
            <StatLabel>Likes</StatLabel>
          </StatCard>
        </StatsGrid>
      </Card>

      {/* Footer Links */}
      <FooterLinks>
        <FooterLink href="/terms">Terms of Service</FooterLink>
        <FooterLink href="/privacy-policy">Privacy Policy</FooterLink>
        <FooterLink href="/communities/help">Help Center</FooterLink>
        <FooterLink href="/">© 2026 Taxsam</FooterLink>
      </FooterLinks>
    </Container>
  );
}
